import React, { useCallback, useContext, useMemo, useState } from 'react'
import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import Heading from 'components/Luxkit/Typography/Heading'
import Pane from 'components/Common/Pane'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import GeoContext from 'contexts/geoContext'
import { getHomeOfferListParams } from 'home/helpers'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import config from 'constants/config'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { encodeSearchParams } from 'lib/search/searchUtils'
import TextButton from 'components/Luxkit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferCarouselLookingForSomethingElse from '../LookingForSomethingElse/OfferCarouselLookingForSomethingElse'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import styled from 'styled-components'
import useCurrentSiteTakeover from 'hooks/useCurrentSiteTakeover'
import BodyText from 'components/Luxkit/Typography/BodyText'
import SiteTakeoverBackgroundImage from 'components/Common/SiteTakeover/SiteTakeoverBackgroundImage'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import FilterChip from 'components/Luxkit/Chips/FilterChip'
import Carousel from 'components/Luxkit/Carousel/Carousel'
import * as Analytics from 'analytics/analytics'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import TextLoadingBox from 'components/Luxkit/Typography/TextLoadingBox'
import cn from 'clsx'
import { TrustPilotStars } from 'components/Common/TrustIndicators/TrustPilotStars'
import { TrustPilotLogoInverse } from 'components/Common/TrustIndicators/TrustPilotLogoInverse'

const SiteTakeoverBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
`

const TrustPilotLogo = styled(TrustPilotLogoInverse)`
  margin-bottom: ${rem(5)};
`

const CarouselContainer = styled(Pane)`
  position: relative;
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
  ${mediaQueryUp.desktop} {
    /**
     * This adds room for the search section on all homepages
     */
    padding-bottom: ${rem(100)};
    /**
     * Search is removed for this experiment 
     */
    &.showTrustExperiment {
      padding-bottom: ${rem(40)};
    }
  }
`

const Content = styled(VerticalSpacer)`
  position: relative;
  z-index: 1;
`

const filterOptions = [
  { label: 'Bonus Dining & Drinks', value: 'Dining & Drinks' },
  { label: 'Family Friendly', value: 'Family Friendly' },
  { label: 'Sun & Beach', value: 'Sun & Beach' },
]

interface Props {
  className?: string;
  overrideTitle?: React.ReactNode;
  overrideOfferList?: App.OfferList;
}

function HeroOfferCarouselSection(props: Props) {
  const { className, overrideTitle, overrideOfferList } = props
  const { currentRegionCode, geoStateName } = useContext(GeoContext)
  const [selectedFilter, setSelectedFilter] = useState<string | undefined>()
  const overrideOfferListFetching = !!overrideOfferList?.fetching

  const toggleFilter = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    const filter = e.currentTarget.dataset.value!
    const nextFilter = selectedFilter === filter ? undefined : filter
    if (nextFilter) {
      Analytics.trackClientEvent({
        subject: 'click_button',
        action: filter,
        type: 'interaction',
        category: 'homepage',
      })
    }
    setSelectedFilter(nextFilter)
  }, [selectedFilter])

  const filters = useMemo<App.OfferListFilters>(() => {
    return {
      ...getHomeOfferListParams({
        region: currentRegionCode,
        subregion: geoStateName,
      }),
      holidayTypes: selectedFilter ? [selectedFilter] : undefined,
    }
  }, [currentRegionCode, geoStateName, selectedFilter])

  const siteTakeover = useCurrentSiteTakeover()

  const searchUrl = useMemo(() => {
    const queryParams = {
      searchItem: ANYWHERE_SEARCH_ITEM,
      rooms: [config.search.defaultOccupants],
    }

    const query = encodeSearchParams(queryParams)
    return `/search?${query.toString()}`
  }, [])

  const carouselPalette = siteTakeover?.palette ?? 'inverse'
  const title = overrideTitle ?? siteTakeover?.heroCarousel.title ??
    (isAgentHubEnabled ? <>Today's <i>featured</i> escapes</> : <>Today's top <i>exclusive</i> offers</>)
  const subtitle = siteTakeover?.heroCarousel.subtitle
  const takeOverLogo = siteTakeover?.heroCarousel.logo
  const isEmergingTrustExperimentActive = !!useOptimizelyExperiment(OptimizelyExperiments.croEmergingMarketTrustIndicatorsV2)
  const showTrustExperiment = !siteTakeover && isEmergingTrustExperimentActive
  const showFilters = !overrideOfferList && (!siteTakeover || siteTakeover.showHeroFilters)

  return (
    <CarouselContainer paletteType={carouselPalette} className={cn({ showTrustExperiment }, className)}>
      {siteTakeover && <SiteTakeoverBackground>
        <SiteTakeoverBackgroundImage background="hero" />
      </SiteTakeoverBackground>}
      <Content gap={24}>
        <LayoutContainer>
          <VerticalSpacer gap={16}>
            <Group
              direction="horizontal"
              horizontalAlign="space-between"
              verticalAlign={isEmergingTrustExperimentActive ? 'end' : 'center'}
              gap={16}
            >
              {showTrustExperiment && <Group direction="horizontal" gap={16} verticalAlign="center">
                <VerticalSpacer gap={24}>
                  {overrideOfferListFetching ? <TextLoadingBox typography="heading2" width="15ch" /> : <Heading variant="heading2">{title}</Heading>}
                  <Group direction="horizontal" gap={8} verticalAlign="center" wrap="wrap">
                    {overrideOfferListFetching && <TextLoadingBox typography="body-medium" width={230} />}
                    {!overrideOfferListFetching && <>
                      <TrustPilotLogo height={26}/>
                      <TrustPilotStars width="115" height="25" />
                      <BodyText variant="medium">10K reviews {currentRegionCode === 'SG' && '• Trusted by 100,000+ Singaporeans'}</BodyText>
                    </>}
                  </Group>
                </VerticalSpacer>
              </Group>}
              {!showTrustExperiment && <Group direction="horizontal" gap={16} verticalAlign="center">
                {takeOverLogo && takeOverLogo}
                <VerticalSpacer gap={2}>
                  {overrideOfferListFetching ? <TextLoadingBox typography="heading2" width="15ch" /> : <Heading variant="heading2">{title}</Heading>}
                  {!!subtitle && <BodyText variant="large">{subtitle}</BodyText>}
                </VerticalSpacer>
              </Group>}
              <CSSBreakpoint min="tablet">
                <Group direction="horizontal" gap={16} verticalAlign="center">
                  {showFilters && !overrideOfferListFetching && <CSSBreakpoint min="desktop">
                    <Group direction="horizontal" gap={8}>
                      {filterOptions.map(option =>
                        <FilterChip
                          key={option.value}
                          size="medium"
                          selected={option.value === selectedFilter}
                          data-value={option.value}
                          onClick={toggleFilter}
                        >
                          {option.label}
                        </FilterChip>,
                      )}
                    </Group>
                  </CSSBreakpoint>}
                  <TextButton
                    kind="tertiary"
                    to={searchUrl}
                  >
                    View all
                  </TextButton>
                </Group>
              </CSSBreakpoint>
            </Group>
            {showFilters && !overrideOfferListFetching && <CSSBreakpoint max="tablet">
              <Carousel gap={8} snap="start">
                {filterOptions.map(option =>
                  <FilterChip
                    key={option.value}
                    size="medium"
                    selected={option.value === selectedFilter}
                    data-value={option.value}
                    onClick={toggleFilter}
                  >
                    {option.label}
                  </FilterChip>,
                )}
              </Carousel>
            </CSSBreakpoint>}
          </VerticalSpacer>
        </LayoutContainer>
        <OfferListCarousel
          overrideOfferList={overrideOfferList}
          filters={filters}
          tileStyle="card-medium"
        >
          <OfferCarouselLookingForSomethingElse to={searchUrl} />
        </OfferListCarousel>
        <CSSBreakpoint max="mobile">
          <TextButton
            kind="tertiary"
            to={searchUrl}
          >
            View all
          </TextButton>
        </CSSBreakpoint>
      </Content>
    </CarouselContainer>
  )
}

export default React.memo(HeroOfferCarouselSection)
